import { useEffect, useRef, useState, useCallback } from 'react';
import styles from './ClosedLoopSection.module.css';
import TopLine from '../../../assets/newDesktopView/topline.png';
import SideLine from '../../../assets/newDesktopView/leftline.png';

/* ── Assets ──────────────────────────────────────────────── */
const LOGO_LEFT = '/assets/school-ai-logo-left-white.svg';
const LOGO_RIGHT = '/assets/school-ai-logo-right-white.svg';
const BY_COSCHOOL = '/assets/by-coschool-white.svg';

/* ── 7 values with positions (from Figma, full-frame coords, center-based)
   Clockwise from Assign (9 o'clock) → up → right → down ─── */
const VALUES = [
  {
    name: 'Assign',
    desc: 'Teacher sets goals, assigns practice work',
    dot: [34.57, 504.9],
    labelX: 44, labelY: 541.4,
  },
  {
    name: 'Practise',
    desc: 'AI helps students learn, prevents cheating',
    dot: [158.57, 390.9],
    labelX: 162, labelY: 425.5,
  },
  {
    name: 'Evidence',
    desc: 'Teacher sees gaps - by student, by concept',
    dot: [308.57, 422.9],
    labelX: 306, labelY: 457.5,
  },
  {
    name: 'Intervene',
    desc: 'Teacher assigns personalised tasks in one click',
    dot: [373.57, 538.9],
    labelX: 348, labelY: 574.19,
  },
  {
    name: 'Inform',
    desc: 'Parents get specific, actionable nudges',
    dot: [328.57, 677.9],
    labelX: 329, labelY: 713.19,
  },
  {
    name: 'Adapt',
    desc: "Next day's class shaped by learning evidence",
    dot: [188.57, 737.9],
    labelX: 189, labelY: 773.19,
  },
  {
    name: 'Govern',
    desc: 'Leadership sees patterns, receives actionable insights',
    dot: [56.57, 660.9],
    labelX: 57, labelY: 696.19,
  },
];

const TOTAL_STEPS = VALUES.length + 2; // 7 values + 1 CTA reveal + 1 CTA hold

/**
 * ClosedLoopSection
 * Uses double-isolation scroll context for smooth iOS performance.
 */
export default function ClosedLoopSection({ handleBtnClick }) {
  const outerRef = useRef(null);
  const frameRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);

  // Circle highlights step-by-step as the user scrolls (desktop + mobile);
  // the header/temple stays pinned (stable) via the sticky frame.
  const handleScroll = useCallback(() => {
    const outer = outerRef.current;
    if (!outer) return;

    // Use the custom scroller (.mobile-inner-scroll) if it exists, otherwise fallback to window
    const scroller = outer.closest('.mobile-inner-scroll') || window;
    const rect = outer.getBoundingClientRect();

    // In our isolated wrapper, 'top' is relative to the scroller
    const sectionTop = -rect.top;
    const scrollableHeight = outer.offsetHeight - (scroller.innerHeight || scroller.offsetHeight);

    if (sectionTop < 0) {
      setActiveStep(0);
      return;
    }

    if (scrollableHeight <= 0) return;

    const progress = Math.min(sectionTop / scrollableHeight, 1);
    // Ensure we map progress 0-1 to step 0-9 cleanly
    const step = Math.min(Math.floor(progress * TOTAL_STEPS), TOTAL_STEPS);

    setActiveStep(step);
  }, []);

  const scrollToStep = useCallback((stepIndex) => {
    const outer = outerRef.current;
    if (!outer) return;

    // Detect the scroller (custom inner scroll or window)
    const scroller = outer.closest('.mobile-inner-scroll') || window;
    const viewportHeight = scroller.innerHeight || scroller.offsetHeight;
    const scrollableHeight = outer.offsetHeight - viewportHeight;

    if (scrollableHeight <= 0) return;

    // Calculate target progress (center of the step's range)
    const targetProgress = (stepIndex + 0.5) / TOTAL_STEPS;
    const targetSectionTop = targetProgress * scrollableHeight;

    // Calculate absolute scroll position
    const rect = outer.getBoundingClientRect();
    const currentScroll = scroller === window ? window.scrollY : scroller.scrollTop;
    // rect.top is relative to viewport, so we add currentScroll to get absolute doc position
    const targetScroll = currentScroll + rect.top + targetSectionTop;

    scroller.scrollTo({
      top: targetScroll,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    const outer = outerRef.current;
    const scroller = outer?.closest('.mobile-inner-scroll') || window;

    scroller.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => scroller.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Scale the fixed-size frame so it always fits the viewport on BOTH axes —
  // so it can never overlap the heading or get cut off, even when zoomed.
  // Layout sizing only — does not affect scroll behaviour.
  useEffect(() => {
    const fitFrame = () => {
      const frame = frameRef.current;
      if (!frame) return;
      const isDesktop = window.innerWidth >= 769;
      const frameW = isDesktop ? 700 : 393;
      const frameH = isDesktop ? 1090 : 840;
      const fit = Math.min(
        window.innerWidth / frameW,
        (window.innerHeight - 40) / frameH
      );
      // Fit the box to the viewport so the whole loop is visible while pinned.
      const scale = Math.max(0.1, Math.min(isDesktop ? 1.2 : 1.15, fit));
      frame.style.transform = `scale(${scale}) translateZ(0)`;
    };
    fitFrame();
    window.addEventListener('resize', fitFrame);
    return () => window.removeEventListener('resize', fitFrame);
  }, []);

  const circleProgress = Math.min(activeStep / VALUES.length, 1);
  const showCTA = activeStep > VALUES.length;

  return (
    <section ref={outerRef} className={styles.outer}>
      {/* ── Intro Eyebrow (Normal Flow - scrolls away) ────────── */}
      <div className={styles.eyebrowWrapper}>
        <span className={styles.eyebrow}>INTRODUCING</span>
      </div>

      <div className={styles.sticky}>
        {/* Side gradient glows — inside the pinned viewport (desktop) */}
        <div className={styles.sideGlowLeft} aria-hidden="true" />
        <div className={styles.sideGlowRight} aria-hidden="true" />

        <div ref={frameRef} className={styles.frame}>
          {/* Background gradient orbs */}
          <div className={styles.orbTL} aria-hidden="true" />
          <div className={styles.orbTR} aria-hidden="true" />
          <div className={styles.orbBL} aria-hidden="true" />

          {/* Temple frame — bracket that frames the header (desktop) */}
          <div className={styles.templeFrame} aria-hidden="true">
            <img src={TopLine} alt="" className={styles.templeTop} />
            <img src={SideLine} alt="" className={styles.templeLeft} />
            <img src={SideLine} alt="" className={styles.templeRight} />
          </div>

          {/* ── Branding + Heading (Unified Sticky) ────────── */}
          <div className={styles.headerUnified}>
            {/* Desktop: INTRODUCING pinned at top of the stack (stable, not scrolling) */}
            <span className={`${styles.eyebrow} ${styles.eyebrowDesktop}`}>INTRODUCING</span>

            {/* SchoolAI logo (white) */}
            <div className={styles.brandLogo} aria-label="School AI by CoSchool">
              <div className={styles.brandLogoMain}>
                <img src={LOGO_LEFT} alt="School AI" className={styles.logoLeft} width="138" loading="lazy" />
                <img src={LOGO_RIGHT} alt="" className={styles.logoRight} width="41" aria-hidden="true" loading="lazy" />
              </div>
              <div className={styles.brandByRow}>
                <span className={styles.brandByText}>by</span>
                <img src={BY_COSCHOOL} alt="CoSchool" width="73" height="13" loading="lazy" />
              </div>
            </div>

            <h2 className={styles.heading}>
              Closed-Loop Learning Platform for Schools
            </h2>
          </div>

          {/* ── Circle + values ──────────────────────────── */}
          <div className={styles.circleArea}>
            <svg
              className={styles.circleRing}
              viewBox="0 0 352.269 352.269"
              fill="none"
            >
              <defs>
                <linearGradient id="grayGrad" x1="63.7" y1="0" x2="278.9" y2="387.6" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#000" />
                  <stop offset="1" stopColor="#666" />
                </linearGradient>
              </defs>
              <circle
                cx="176.135"
                cy="176.135"
                r="174.6"
                stroke={circleProgress >= 1 ? '#9582FF' : 'url(#grayGrad)'}
                strokeWidth="3"
                style={{
                  transition: 'stroke 0.6s ease',
                  filter: circleProgress >= 1 ? 'drop-shadow(0 0 8px rgba(149,130,255,0.4))' : 'none',
                }}
              />
            </svg>

            {VALUES.map((v, i) => {
              const isSeen = i < activeStep;
              const isActive = i === activeStep - 1 && activeStep <= VALUES.length;
              const isVisible = isSeen || showCTA;

              return (
                <div key={v.name}>
                  <div
                    className={`${styles.numCircle} ${isVisible ? styles.numCircleActive : ''}`}
                    style={{ left: v.dot[0], top: v.dot[1] }}
                    onClick={() => scrollToStep(i + 1)}
                  >
                    {i + 1}
                  </div>
                  <span
                    className={styles.label}
                    style={{
                      left: v.labelX,
                      top: v.labelY,
                      opacity: isActive ? 1 : isVisible || showCTA ? 0.8 : 0.1,
                      color: isActive || isVisible || showCTA ? '#fff' : 'rgba(255,255,255,0.1)',
                    }}
                  >
                    {v.name}
                  </span>
                </div>
              );
            })}

            {/* Center content: number + description or CTA */}
            <div className={styles.centerContent}>
              {!showCTA && activeStep > 0 && activeStep <= VALUES.length && (
                <p key={`desc-${activeStep}`} className={styles.description}>
                  {VALUES[activeStep - 1].desc}
                </p>
              )}
              {showCTA && (
                <a
                  className={`wm-btn-primary ${styles.ctaBtn}`}
                  aria-label="Try SchoolAi — go to contact"
                  onClick={(e) => handleBtnClick(e.target, 'closed-loop-cta')}
                >
                  Try SchoolAi
                </a>
              )}
            </div>
          </div>
          <p className={styles.subTagline}>
            Anchored by Teachers &nbsp;-&nbsp; Supported by Parents &nbsp;-&nbsp; AI enabled
          </p>
        </div>
      </div>
    </section>
  );
}
